import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { API } from "@/App";
import { Users, Upload, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

import {
  StudentFilters,
  StudentsTable,
  AddStudentModal,
  BulkUploadModal
} from "@/components/school/students";

export default function SchoolStudents() {
  const { admin, school } = useOutletContext();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    grade: "",
    section: "",
    status: "",
    search: ""
  });
  const [pagination, setPagination] = useState({
    skip: 0,
    limit: 25,
    total: 0
  });

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  
  // Form data
  const [newStudent, setNewStudent] = useState({
    name: "",
    grade: "",
    section: "A",
    roll_number: "",
    admission_number: "",
    parent_name: "",
    parent_email: "",
    parent_phone: ""
  });

  // Upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadValidation, setUploadValidation] = useState(null);
  const [uploading, setUploading] = useState(false);

  const permissions = admin?.permissions || [];
  const canManage = permissions.includes("manage_students");
  const canUpload = permissions.includes("bulk_upload");

  useEffect(() => {
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.skip]);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.grade) params.append("grade", filters.grade);
      if (filters.section) params.append("section", filters.section);
      if (filters.status) params.append("status", filters.status);
      if (filters.search) params.append("search", filters.search);
      params.append("skip", pagination.skip);
      params.append("limit", pagination.limit);

      const response = await fetch(`${API}/school/students/list?${params}`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setStudents(data.students || []);
        setPagination(prev => ({ ...prev, total: data.total || 0 }));
      } else {
        toast.error("Failed to fetch students");
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    
    if (!newStudent.name || !newStudent.grade) {
      toast.error("Name and Grade are required");
      return;
    }

    try {
      const response = await fetch(`${API}/school/students/enroll`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...newStudent,
          grade: parseInt(newStudent.grade)
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success("Student enrolled successfully");
        setShowAddModal(false);
        setNewStudent({
          name: "", grade: "", section: "A", roll_number: "",
          admission_number: "", parent_name: "", parent_email: "", parent_phone: ""
        });
        fetchStudents();
      } else {
        toast.error(data.error || "Failed to enroll student");
      }
    } catch (error) {
      toast.error("Network error");
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(`${API}/school/students/template`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        
        // Create CSV content
        const headers = [...data.columns.required, ...data.columns.optional].join(",");
        const csvContent = `${headers}\n${data.template}`;
        
        // Download
        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "student_upload_template.csv";
        a.click();
        window.URL.revokeObjectURL(url);
        
        toast.success("Template downloaded");
      }
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const handleValidateCSV = async () => {
    if (!uploadFile) {
      toast.error("Please select a file");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", uploadFile);

    try {
      const response = await fetch(`${API}/school/students/validate-csv`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      const data = await response.json();
      setUploadValidation(data);

      if (data.valid) {
        toast.success(`Valid: ${data.valid_rows} students ready to import`);
      } else {
        toast.error(`Found ${data.errors?.length || 0} errors`);
      }
    } catch (error) {
      toast.error("Validation failed");
    } finally {
      setUploading(false);
    }
  };

  const handleBulkUpload = async () => {
    if (!uploadFile || !uploadValidation?.valid) {
      toast.error("Please validate the file first");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", uploadFile);

    try {
      const response = await fetch(`${API}/school/students/bulk-enroll`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`Successfully enrolled ${data.enrolled_count} students`);
        setShowUploadModal(false);
        setUploadFile(null);
        setUploadValidation(null);
        fetchStudents();
      } else {
        toast.error(data.message || "Upload failed");
      }
    } catch (error) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateStatus = async (studentId, status, reason = "") => {
    try {
      const response = await fetch(`${API}/school/students/${studentId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status, reason }),
      });

      if (response.ok) {
        toast.success("Status updated");
        fetchStudents();
      } else {
        const data = await response.json();
        toast.error(data.error || "Failed to update status");
      }
    } catch (error) {
      toast.error("Network error");
    }
  };

  const handleBulkUpdateStatus = async (studentIds, status) => {
    try {
      const response = await fetch(`${API}/school/students/bulk-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          student_ids: studentIds, 
          status,
          reason: `Bulk ${status === 'active' ? 'activation' : 'deactivation'}`
        }),
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(`${data.updated_count || studentIds.length} students updated`);
        fetchStudents();
      } else {
        toast.error(data.error || "Failed to update students");
      }
    } catch (error) {
      toast.error("Network error");
    }
  };

  const handleEdit = (student) => {
    setSelectedStudent(student);
    setShowEditModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Students</h1>
          <p className="text-slate-500 text-sm">Manage enrolled students</p>
        </div>
        <div className="flex items-center gap-2">
          {canUpload && (
            <Button 
              variant="outline" 
              onClick={() => setShowUploadModal(true)}
              data-testid="bulk-upload-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Bulk Upload
            </Button>
          )}
          {canManage && (
            <Button 
              onClick={() => setShowAddModal(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="add-student-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Student
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <StudentFilters 
        filters={filters} 
        setFilters={setFilters} 
        school={school} 
      />

      {/* Students Table */}
      <StudentsTable
        students={students}
        loading={loading}
        filters={filters}
        pagination={pagination}
        setPagination={setPagination}
        canManage={canManage}
        onEdit={handleEdit}
        onUpdateStatus={handleUpdateStatus}
        onBulkUpdateStatus={handleBulkUpdateStatus}
      />

      {/* Add Student Modal */}
      <AddStudentModal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        newStudent={newStudent}
        setNewStudent={setNewStudent}
        onSubmit={handleAddStudent}
        school={school}
      />

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        open={showUploadModal}
        onOpenChange={setShowUploadModal}
        uploadFile={uploadFile}
        setUploadFile={setUploadFile}
        uploadValidation={uploadValidation}
        setUploadValidation={setUploadValidation}
        uploading={uploading}
        onDownloadTemplate={handleDownloadTemplate}
        onValidate={handleValidateCSV}
        onUpload={handleBulkUpload}
      />
    </div>
  );
}
